import { Link } from 'react-router-dom';
import { siteConfig } from '../config/siteConfig';
import { clubTier } from '../config/pricingTiers';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import SectionHeader from '../components/ui/SectionHeader';
import Card from '../components/ui/Card';
import './ForClubs.css';

/* ── Club features ────────────────────────────────────────────
   The six key things clubs get that individual players don't.
   ─────────────────────────────────────────────────────────── */
const features = [
  {
    title: 'Admin Dashboard',
    description: 'Manage your members, approve or reject logged matches, and keep your club organised from one clean control panel.',
    icon: <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>,
  },
  {
    title: 'Public Leaderboard',
    description: "Every club gets a shareable leaderboard page. Post it in your group chat, Slack, or on the wall — no login required to view.",
    icon: <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  },
  {
    title: 'Automatic ELO Ratings',
    description: "Every match logged instantly recalculates every player's rating. No spreadsheets, no manual updates — it just works.",
    icon: <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
  },
  {
    title: 'Tournament Tools',
    description: "Run brackets and round-robins directly inside RankTT. Results feed straight into player ratings when it's done.",
    icon: <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="3"/><path d="M6.5 17a5.5 5.5 0 0 1 11 0"/><line x1="12" y1="11" x2="12" y2="14"/></svg>,
  },
  {
    title: 'Up to 100 Members',
    description: "Whether it's a 10-person office or a 100-member club, the Club tier handles it. Every member gets their own profile and rating.",
    icon: <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  },
  {
    title: 'Match Scheduling',
    description: 'Challenge other members and schedule matches in advance. Never lose track of who owes who a rematch.',
    icon: <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  },
];

/* ── How it works steps ──────────────────────────────────────── */
const steps = [
  {
    number: '01',
    title: 'Create your club',
    description: "Sign up, name your club, and you're in. Takes about two minutes. No technical setup required.",
  },
  {
    number: '02',
    title: 'Invite your members',
    description: 'Share a link or email invite. Members create free accounts and join your club instantly.',
  },
  {
    number: '03',
    title: 'Start logging matches',
    description: 'After every game, one player logs the result. Ratings update automatically and the leaderboard refreshes in real time.',
  },
];

function ForClubs() {
  return (
    <main className="for-clubs">

      {/* ════════════════════════════════════════════════════
          HERO
          ════════════════════════════════════════════════════ */}
      <section className="fc-hero">
        <div className="fc-glow" aria-hidden="true"></div>
        <div className="section-inner fc-hero-inner">

          <Badge>RankTT for Clubs &amp; Offices</Badge>

          <h1 className="fc-headline">
            Give your club the<br />
            <span className="fc-highlight">leaderboard it deserves</span>
          </h1>

          <p className="fc-subtext">
            Stop tracking results on a whiteboard or spreadsheet. RankTT gives your club
            automatic ELO ratings, a live public leaderboard, and the admin tools to run it
            all — for less than a cup of coffee per day.
          </p>

          <div className="fc-hero-cta">
            <Button to="/signup" size="lg">Set up your club free</Button>
            <Button href="#pricing" variant="outline" size="lg">See Club pricing &rarr;</Button>
          </div>

          <p className="fc-proof">Trusted by clubs, offices, and friend groups worldwide</p>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════
          BEFORE / AFTER
          ════════════════════════════════════════════════════ */}
      <section className="fc-problem">
        <div className="section-inner">
          <div className="fc-problem-grid">

            <Card className="fc-problem-col fc-before">
              <div className="fc-problem-label before-label">Before RankTT</div>
              <ul className="fc-problem-list">
                <li><span className="x-icon">&#10007;</span> Whiteboard rankings nobody updates</li>
                <li><span className="x-icon">&#10007;</span> Spreadsheets only one person can edit</li>
                <li><span className="x-icon">&#10007;</span> Disputes about who's actually the best</li>
                <li><span className="x-icon">&#10007;</span> No history — last week's results are gone</li>
                <li><span className="x-icon">&#10007;</span> Tournaments run on pen and paper</li>
              </ul>
            </Card>

            <div className="fc-problem-divider" aria-hidden="true"><span>vs</span></div>

            <Card className="fc-problem-col fc-after">
              <div className="fc-problem-label after-label">With RankTT</div>
              <ul className="fc-problem-list">
                <li><span className="check-icon">&#10003;</span> Live leaderboard, always up to date</li>
                <li><span className="check-icon">&#10003;</span> Anyone can log a match from their phone</li>
                <li><span className="check-icon">&#10003;</span> ELO rating settles every debate</li>
                <li><span className="check-icon">&#10003;</span> Full history for every player, forever</li>
                <li><span className="check-icon">&#10003;</span> Built-in tournament bracket tools</li>
              </ul>
            </Card>

          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════
          FEATURES
          ════════════════════════════════════════════════════ */}
      <section className="fc-features" id="features">
        <div className="section-inner">
          <SectionHeader
            title="Everything your club needs"
            subtitle="One platform to run your entire table tennis community."
          />
          <div className="fc-features-grid">
            {features.map((feature, i) => (
              <Card key={i} className="fc-feature-card">
                <div className="fc-feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════
          HOW IT WORKS
          ════════════════════════════════════════════════════ */}
      <section className="fc-how-it-works">
        <div className="section-inner">
          <SectionHeader
            title="Up and running in minutes"
            subtitle="No IT department needed. If you can send an email, you can set up a RankTT club."
          />
          <div className="fc-steps">
            {steps.map((step, i) => (
              <div className="fc-step" key={i}>
                <div className="fc-step-number">{step.number}</div>
                <div className="fc-step-content">
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
                {i < steps.length - 1 && (
                  <div className="fc-step-connector" aria-hidden="true"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════
          PRICING CALLOUT
          Uses clubTier from pricingTiers config — not hardcoded.
          ════════════════════════════════════════════════════ */}
      <section className="fc-pricing" id="pricing">
        <div className="section-inner">
          <SectionHeader
            title="One plan built for clubs"
            subtitle="Everything you need, nothing you don't."
          />

          <Card highlighted className="fc-pricing-card">
            <div className="fc-pricing-left">
              <div className="fc-tier-name">{clubTier.name}</div>
              <div className="fc-tier-price">
                <span className="fc-price-amount">{clubTier.price}</span>
                <span className="fc-price-period">/{clubTier.period}</span>
              </div>
              <p className="fc-tier-desc">
                {clubTier.description} Covers your entire club — not per player.
              </p>
              <Button to="/signup" size="lg" fullWidth>Start your free trial</Button>
              <p className="fc-pricing-note">No credit card required. Cancel anytime.</p>
            </div>

            <ul className="fc-pricing-features">
              {clubTier.features.map((feature, i) => (
                <li key={i}><span className="check-icon">&#10003;</span> {feature}</li>
              ))}
            </ul>
          </Card>

          <p className="fc-pricing-alt">
            Not ready for a full club plan?{' '}
            <Link to="/" className="fc-text-link">Start free as an individual &rarr;</Link>
          </p>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════
          FINAL CTA
          ════════════════════════════════════════════════════ */}
      <section className="fc-final-cta">
        <div className="section-inner fc-final-cta-inner">
          <h2>Ready to level up your club?</h2>
          <p>Join clubs and offices already running their table tennis communities on RankTT.</p>
          <Button to="/signup" size="lg">Set up your club free &rarr;</Button>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════
          FOOTER
          ════════════════════════════════════════════════════ */}
      <footer className="footer">
        <div className="footer-inner">
          <Link to="/" className="footer-logo">Rank<span>TT</span></Link>
          <nav className="footer-links">
            <a href="/#features">Features</a>
            <a href="/#pricing">Pricing</a>
            <Link to="/clubs">For Clubs</Link>
            <Link to="/privacy-policy">Privacy Policy</Link>
            <Link to="/terms-of-service">Terms of Service</Link>
          </nav>
          <p className="footer-copy">{siteConfig.copyright}</p>
        </div>
      </footer>

    </main>
  );
}

export default ForClubs;
